/**
 * @component CreateOffer
 * @description
 * Handles creation, editing, and viewing of offers with dynamic discount options.
 * Supports group-based discounts and rate-type references.
 *
 * @props
 * - mode: 'create' | 'edit' | 'view' — determines the component mode
 * - record: existing offer record (for edit/view modes)
 * - visible: boolean — modal visibility
 * - onClose: function — callback to close the modal
 * - reload: function — callback to refresh the parent table/list after save
 */

import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Row, Col, Checkbox, message, Radio, Table } from 'antd';
import { Items, ItemRateTypes } from '../../models';
import { ReferenceSelect } from 'soxo-bootstrap-core';

const CreateOffer = ({ mode = 'create', record, visible, onClose, reload }) => {
    const [discountMode, setDiscountMode] = useState('all'); // 'all' or 'group'
    const [groupOptions, setGroupOptions] = useState([]); // Available item groups for group discount
    const [groupDiscounts, setGroupDiscounts] = useState({}); // Holds discount % per group
    const [form] = Form.useForm();
    const { Option } = Select;

    const isView = mode === 'view';
    const isEdit = mode === 'edit';

    // Watch discount type for dynamic field rendering
    const discountType = Form.useWatch('discountType', form);
    // Dropdown options for discount type
    const discountTypeOptions = [
        { label: 'None', value: 'N' },
        { label: 'Discount (%)', value: 'D' },
        { label: 'Based on other rate type', value: 'RA' },
        { label: 'Discount % based on other rate type', value: 'DA' },
    ];

    /**
     * Fetch item groups when discount type is "DA" (Discount % based on other rate type)
     * and the discount mode is "group".
     */
    useEffect(() => {
        if (discountType === 'DA' && discountMode === 'group') {
            const loadGroups = async () => {
                try {
                    const response = await Items.getItemGroup();
                    const groups = response?.result || [];
                    const formatted = groups.map((g, index) => ({
                        key: g.itmg_slno || index + 1,
                        group_pointer: g.itmg_slno,
                        code: g.itmg_slno,
                        name: g.itmg_desc,
                        discount: groupDiscounts[g.itmg_slno] || '',
                    }));
                    setGroupOptions(formatted);
                } catch (err) {
                    console.error('Error fetching item groups:', err);
                }
            };
            loadGroups();
        } else {
            setGroupOptions([]);
        }
    }, [discountType, discountMode]);

    /**
     * Populate form fields when editing or viewing an offer.
     * Resets the form and discount state when creating a new offer.
     */
    useEffect(() => {
        if (record) {
            form.setFieldsValue({
                code: record.code,
                description: record.description,
                discountType: record.discountType,
                discount: record.discount,
                remarks: record.remarks,
                refRateType: record.ref_rate_type,
                slno: record.slno,
                isDefault: record.isdefault === 'Y',
                active: record.active
            });
            setDiscountMode(record.discount_type_mode?.toLowerCase() || 'all');

            // Preload group discounts if available
            if (record.group_details?.length) {
                const map = {};
                record.group_details.forEach((g) => (map[g.group_pointer] = g.discount));
                setGroupDiscounts(map);
            }
        } else {
            form.resetFields();
            setDiscountMode('all');
            setGroupDiscounts({});
        }
    }, [record, form]);

    /**
     * Updates discount % for a specific group inside the group discount table.
     * @param {number} group_pointer - The unique ID of the group.
     * @param {string|number} value - The entered discount percentage.
     */
    const handleGroupDiscountChange = (group_pointer, value) => {
        setGroupDiscounts((prev) => ({ ...prev, [group_pointer]: value }));
    };

    // Table columns
    const columns = [
        { title: 'Code', dataIndex: 'code', key: 'code' },
        { title: 'Name', dataIndex: 'name', key: 'name' },
        {
            title: 'Enter Discount %',
            dataIndex: 'discount',
            key: 'discount',
            render: (_, record) => (
                <Input
                    type="number"
                    placeholder="Enter %"
                    value={groupDiscounts[record.group_pointer] || ''}
                    onChange={(e) => handleGroupDiscountChange(record.group_pointer, e.target.value)}
                    disabled={isView}
                />
            ),
        },
    ];

    /**
     * Handles form submission for creating or updating an offer.
     * Validates input, prepares payload, calls API, and provides user feedback.
     */
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const payload = {
                code: values.code,
                description: values.description,
                active: values.active ? 'Y' : 'N',
                remarks: values.remarks || '',
                discount_type: values.discountType,
                discount: Number(values.discount || 0),
                isdefault: values.isDefault ? 'Y' : 'N',
                slno: Number(values.slno || 0),
                ref_rate_type: values.refRateType,
                discount_type_mode: values.discountType === 'DA' && discountMode === 'group' ? 'GRP' : 'All',
                group_details:
                    values.discountType === 'DA' && discountMode === 'group'
                        ? groupOptions.map((g) => ({
                              group_pointer: g.group_pointer,
                              discount: Number(groupDiscounts[g.group_pointer] || 0),
                          }))
                        : [],
            };

            const res = await Items.createOffer(payload);

            if (res?.result?.success) {
                message.success(res?.result?.message || 'Offer saved successfully');
                form.resetFields();
                setGroupDiscounts({});
                reload?.();
                onClose();
            } else {
                message.error(res?.result?.message || 'Failed to save offer');
            }
        } catch (err) {
            console.error('Error saving offer:', err);
            message.error('Error saving offer');
        }
    };

    return (
        <Modal
            title={isView ? 'View Offer' : isEdit ? 'Edit Offer' : 'Create New Offer'}
            open={visible}
            onCancel={onClose}
            onOk={isView ? onClose : handleSubmit}
            okText={isView ? 'Close' : 'Submit'}
            width={700}
        >
            <Form form={form} layout="vertical" disabled={isView}>
                <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Please enter offer code' }]}>
                    <Input placeholder="Enter code" disabled={isEdit||isView}/>
                </Form.Item>
                <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Please enter description' }]}>
                    <Input placeholder="Enter offer description" />
                </Form.Item>
                <Form.Item name="discountType" label="Discount Type" rules={[{ required: true, message: 'Select discount type' }]}>
                    <Select placeholder="Select discount type">
                        {discountTypeOptions.map((type) => (
                            <Option key={type.value} value={type.value}>
                                {type.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                 <Form.Item
            name="discount"
            label="Discount (%)"
            rules={[
                { required: true, message: 'Enter discount value' },
                {
                    validator: (_, value) =>
                        value >= 0 ? Promise.resolve() : Promise.reject(new Error('Discount cannot be negative')),
                },
            ]}
        >
                            <Input type="number" min={0} //prevent negative
                             placeholder="Enter discount %" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="slno" label="Sl No"
                          rules={[
                {
                    validator: (_, value) =>
                        value === undefined || value === null || value >= 0
                            ? Promise.resolve()
                            : Promise.reject(new Error('Sl No cannot be negative')),
                },
            ]}
                        >
                            <Input type="number" 
                              min={0} // Prevent negative 
                            placeholder="Enter sl no" />
                        </Form.Item>
                    </Col>
                </Row>

                {['DA', 'RA'].includes(discountType) && (
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="refRateType"
                                label="Ref. Rate Type"
                                rules={[{ required: true, message: 'Please select Ref. Rate Type' }]}
                            >
                                <ReferenceSelect
                                    label="itmrt_desc"
                                    mode="nura-base"
                                    config={{ queries: [{ field: 'itmrt_active', value: 'Y' }] }}
                                    model={ItemRateTypes}
                                    field="itmrt_code"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {discountType === 'DA' && (
                    <>
                        <Radio.Group value={discountMode} onChange={(e) => setDiscountMode(e.target.value)} style={{ marginBottom: 12 }}>
                            <Radio value="all">All</Radio>
                            <Radio value="group">Group</Radio>
                        </Radio.Group>

                        {discountMode === 'group' && (
                            <Table columns={columns} dataSource={groupOptions} pagination={false} bordered style={{ marginTop: 12 }} rowKey="key" />
                        )}
                    </>
                )}

                <Form.Item name="remarks" label="Remarks">
                    <Input.TextArea placeholder="Enter remarks" />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="isDefault" valuePropName="checked">
                            <Checkbox disabled>Is Default</Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="active" valuePropName="checked">
                            <Checkbox>Active</Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default CreateOffer;
